import React, { useState, useEffect } from "react";
import { type NodeProps, Node, useReactFlow } from "@xyflow/react";
import { Database, X } from "lucide-react";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";

export const MongoDBNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const { setNodes } = useReactFlow();

  const [connectionString, setConnectionString] = useState(
    data.connection_string || "",
  );
  const [databaseName, setDatabaseName] = useState(data.database_name || "");
  const [collectionName, setCollectionName] = useState(
    data.collection_name || "",
  );
  const [inputMapping, setInputMapping] = useState<string>(
    (data.results as string) || "",
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // ── Sync config to ReactFlow ──────────────────────────────────────
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                connection_string: connectionString,
                database_name: databaseName,
                collection_name: collectionName,
                results: inputMapping,
              },
            }
          : node,
      ),
    );
  }, [connectionString, databaseName, collectionName, inputMapping, id, setNodes]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");
    if (variable) setInputMapping(variable);
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const isConfigured = !!(connectionString && databaseName && collectionName && inputMapping);

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="MongoDB Connector"
      icon={<Database className="w-4 h-4 text-emerald-600" />}
      nodeDocsType="FILE-DATABASE"
      themeColor="emerald"
      configureLabel="Configure MongoDB"
      isConfigured={isConfigured}
      validationErrors={[
        ...(!inputMapping ? ["Input data is required"] : []),
        ...(!connectionString ? ["Connection string is required"] : []),
        ...(!databaseName ? ["Database name is required"] : []),
        ...(!collectionName ? ["Collection name is required"] : []),
      ]}

      statusText={{
        running: "Executing...",
        success: "Executed Successfully",
        error: "Execution Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >

      {/* Modal */}
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        nodeId={id}
        title="MongoDB Connector - Insert documents"
        icon={<Database className="w-5 h-5 text-green-600" />}
        themeColor="emerald"
        nodeDocsType="FILE-DATABASE"
        validationStatus={{
          isValid: isConfigured,
          errors: [
            ...(!inputMapping ? ["Input data is required"] : []),
            ...(!connectionString ? ["Connection string is required"] : []),
            ...(!databaseName ? ["Database name is required"] : []),
            ...(!collectionName ? ["Collection name is required"] : []),
          ],
        }}
        saveDisabled={!isConfigured}
        nodeData={data}
        nodeIcon={<Database className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No outputs yet"
      >
        <div className="space-y-5 mt-2">
          {/* Input Data */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Input Data <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                isDraggingOver
                  ? "border-green-400 bg-green-50"
                  : inputMapping
                    ? "border-green-300 bg-green-50/50"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400"
              }`}
            >
              {inputMapping ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Selected input
                    </div>
                    <code className="text-xs text-green-700 font-mono break-all">
                      {inputMapping}
                    </code>
                  </div>
                  <button
                    onClick={() => setInputMapping("")}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-400">
                  <span className="text-sm">Drag and drop input data here</span>
                  <span className="text-xs mt-1">From connected node outputs</span>
                </div>
              )}
            </div>
            {!inputMapping && (
              <p className="text-xs text-red-500 mt-1">Input data is required</p>
            )}
          </div>

          {/* Connection String */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Connection String <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={connectionString}
              onChange={(e) => setConnectionString(e.target.value)}
              placeholder="mongodb://user:pass@host:port"
              className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm text-gray-700 hover:border-green-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition ${!connectionString ? "border-red-300" : "border-gray-300"}`}
            />
            {!connectionString && (
              <p className="text-xs text-red-500 mt-1">Connection string is required</p>
            )}
          </div>

          {/* Database and Collection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Database <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={databaseName}
                onChange={(e) => setDatabaseName(e.target.value)}
                placeholder="e.g., analytics"
                className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm text-gray-700 hover:border-green-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition ${!databaseName ? "border-red-300" : "border-gray-300"}`}
              />
              {!databaseName && (
                <p className="text-xs text-red-500 mt-1">Database name is required</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Collection <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                placeholder="e.g., documents"
                className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm text-gray-700 hover:border-green-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition ${!collectionName ? "border-red-300" : "border-gray-300"}`}
              />
              {!collectionName && (
                <p className="text-xs text-red-500 mt-1">Collection name is required</p>
              )}
            </div>
          </div>
        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default MongoDBNode;
